import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FundPerformance } from 'src/app/domain/fund-performance/dto/FundPerformance';
import { ModalButtons } from 'src/app/domain/shared/component/modal-container/modal-container.component';

@Component({
  selector: 'app-modal-expenses-prediction',
  templateUrl: './modal-expenses-prediction.component.html',
  styleUrls: ['./modal-expenses-prediction.component.scss'],
})

export class ModalExpensesPredictionComponent implements OnChanges {

  @Input() updatedFundPerformance: FundPerformance;
  @Input() fundPerformanceList: FundPerformance[];
  @Output() expensesEmitter = new EventEmitter<{
    totalExpenses: number;
    mgmFees: number;
    aggregatedOrgExpenses: number;
  }>();

  @Output() closeModalEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  header = 'Expenses prediction';
  selectedPeriodFirst: Date;
  selectedPeriodSecond: Date;
  predictedPeriod: Date;
  periodList: Date[] = [];
  periodListFirst: Date[] = [];
  periodListSecond: Date[] = [];
  predictedExpenses = 0;
  mgmFees = 0;
  aggregatedOrgExpenses = 0;
  otherExpenses = 0;
  firstFundPerformance: FundPerformance;
  secondFundPerformance: FundPerformance;

  modalButtons: ModalButtons = {
    dismiss: 'Cancel',
    apply: 'Apply',
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.updatedFundPerformance) {
      this.predictedPeriod = this.updatedFundPerformance.period;
    }
    if (changes.fundPerformanceList) {
      if (this.fundPerformanceList && this.fundPerformanceList.length > 0) {
        this.getPeriods();
        this.calculateExpenses();
      }
    }
  }

  /**
   * Create list of periods.
   */
  getPeriods(): void {
    this.periodList = [];
    for (const fundPerformance of this.fundPerformanceList) {
      if (fundPerformance.period < this.predictedPeriod) {
        this.periodList.push(fundPerformance.period);
      }
    }
    this.periodList.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    this.selectedPeriodFirst = this.periodList[1];
    this.selectedPeriodSecond = this.periodList[0];
    this.periodList.forEach((p) => {
      if (p !== this.selectedPeriodSecond) {
        this.periodListFirst.push(p);
      }
      if (p !== this.selectedPeriodFirst) {
        this.periodListSecond.push(p);
      }
    });
    this.firstFundPerformance = this.fundPerformanceList.filter((f) => f.period === this.selectedPeriodFirst)[0];
    this.secondFundPerformance = this.fundPerformanceList.filter((f) => f.period === this.selectedPeriodSecond)[0];
  }


  /**
   * Update first date.
   *
   * @param interval :Date to be updated
   */
  switchPerformanceForFirstDate(period: Date): void {
    if (!period) {
      return;
    }
    this.selectedPeriodFirst = period;
    this.firstFundPerformance = this.fundPerformanceList.find((f) => f.period === this.selectedPeriodFirst);
    this.periodListSecond = [];
    this.periodList.forEach((p) => {
      if (this.selectedPeriodSecond !== p) {
        this.periodListSecond.push(p);
      }
    });
    this.calculateExpenses();
  }

  /**
   * Update second date.
   *
   * @param interval :Date to be updated
   */
  switchPerformanceForSecondDate(period: Date): void {
    if (!period) {
      return;
    }
    this.selectedPeriodSecond = period;
    this.secondFundPerformance = this.fundPerformanceList.find((f) => f.period === this.selectedPeriodSecond);
    this.periodListFirst = [];
    this.periodList.forEach((p) => {
      if (p < this.selectedPeriodSecond) {
        this.periodListFirst.push(p);
      }
    });
    if (!this.selectedPeriodFirst?.toString() || this.selectedPeriodFirst?.toString() >= this.selectedPeriodSecond.toString()) {
      this.selectedPeriodFirst = this.periodListFirst[0];
      this.switchPerformanceForFirstDate(this.selectedPeriodFirst);
      if (!this.selectedPeriodFirst) {
        this.firstFundPerformance = new FundPerformance();
      }
    }
    this.calculateExpenses();
  }

  calculateExpenses(): void {
    this.predictedExpenses = 0;
    this.mgmFees = 0;
    this.aggregatedOrgExpenses = 0;
    this.otherExpenses = 0;
    if (this.selectedPeriodFirst && this.selectedPeriodSecond) {
      const baseDays = this.countDaysBetween(this.selectedPeriodFirst, this.selectedPeriodSecond);
      const predictedDays = this.countDaysBetween(this.selectedPeriodSecond, this.predictedPeriod);
      if (this.firstFundPerformance.totalExpenses && this.secondFundPerformance.totalExpenses) {
        const totalExpenses = this.secondFundPerformance.totalExpenses - this.firstFundPerformance.totalExpenses;
        this.predictedExpenses = totalExpenses * predictedDays / baseDays + this.secondFundPerformance.totalExpenses;
      }
      if (this.firstFundPerformance.mgmFees && this.secondFundPerformance.mgmFees) {
        const mgmFees = this.secondFundPerformance.mgmFees - this.firstFundPerformance.mgmFees;
        this.mgmFees = mgmFees * predictedDays / baseDays + this.secondFundPerformance.mgmFees;
      }
      if (this.firstFundPerformance.aggregatedOrgExpenses && this.secondFundPerformance.aggregatedOrgExpenses) {
        const aggregatedOrgExpenses = this.secondFundPerformance.aggregatedOrgExpenses - this.firstFundPerformance.aggregatedOrgExpenses;
        this.aggregatedOrgExpenses = aggregatedOrgExpenses * predictedDays / baseDays + this.secondFundPerformance.aggregatedOrgExpenses;
      }
      this.otherExpenses = this.predictedExpenses - this.mgmFees - this.aggregatedOrgExpenses;
    }
  }

  countDaysBetween(fromDate: Date, toDate: Date): number {
    const oneDay = 24 * 60 * 60 * 1000;
    const difference = Math.round(Math.abs((new Date(fromDate).getTime() - new Date(toDate).getTime()) / oneDay));
    return difference;
  }

  close(value: boolean): void {
    if (value && this.selectedPeriodFirst && this.selectedPeriodSecond) {
      const expenses = {
        totalExpenses: Math.round(this.predictedExpenses),
        mgmFees: Math.round(this.mgmFees),
        aggregatedOrgExpenses: Math.round(this.aggregatedOrgExpenses),
      };
      this.expensesEmitter.emit(expenses);
    } else {
      this.closeModalEmitter.emit(true);
    }
  }

}
